import {
  CodexCategorySchema,
  CodexEntrySchema,
  CodexFieldSchema,
  CodexFieldType,
  NovelActSchema,
  NovelActTree,
  NovelChapterSchema,
  NovelSceneSchema,
  NovelSchema,
  NovelSeriesSchema,
  SnippetSchema,
} from '#/index';
import {
  CodexDetail,
  NovelLabel,
  OutlineTree,
  PointOfView,
} from '../../types';
import { getCodexEntryFields, getNovelActTree } from '../../utils';
import { convertHtmlToFormattedText, convertJSONContentToFormattedText } from '@/utils';
import { FormatterRenderer } from '..';

type PlainRenderer<T> = FormatterRenderer<T>;

const withFallback = (value: string | undefined, fallback: string): string =>
  value && value.trim().length ? value : fallback;

const actTreeToPlain = (actTrees: NovelActTree[], mode: 'content' | 'summary', listType?: 'unordered' | 'ordered'): string => {
  const lines: string[] = [];

  const listPrefix = (index: number) => listType
    ? listType === 'unordered' ? '-' : `${index + 1}.`
    : '';

  const formatAct = (act: NovelActTree, actIndex: number): void => {
    if (actIndex > 0) {
      lines.push(''); // 空行
    }
    lines.push(`${listPrefix(actIndex)} ${act.fullName}`);
    act.children?.forEach((chapter, chapterIndex) => {
      formatChapter(chapter, chapterIndex);
    });
  };

  const formatChapter = (chapter: NovelActTree, chapterIndex: number): void => {
    if (chapterIndex > 0) {
      lines.push(''); // 空行
    }
    lines.push(`  ${listPrefix(chapterIndex)} ${chapter.fullName}`);
    lines.push(''); // 空行

    chapter.children?.forEach((scene, sceneIndex) => {
      formatScene(scene, sceneIndex);
    });
  };

  const formatScene = (scene: NovelActTree, sceneIndex: number): void => {
    const sceneData = scene.data as NovelSceneSchema;

    // 构建场景标题：Chapter X - Scene Y
    let sceneLine = `    ${listPrefix(sceneIndex)} ${scene.fullName}`;

    // 获取场景内容（summary 或 content）
    let sceneContent = '';
    if (mode === 'content' && sceneData.attributes.content) {
      sceneContent = convertJSONContentToFormattedText(sceneData.attributes.content);
    } else if (mode === 'summary' && sceneData.attributes.summary) {
      sceneContent = convertJSONContentToFormattedText(sceneData.attributes.summary);
    }

    sceneLine += `:${sceneContent}`;
    lines.push(sceneLine);
  };


  actTrees.forEach((act, actIndex) => {
    formatAct(act, actIndex);
  });
  return lines.join('\n');
}


export const renderNovelPlain: PlainRenderer<NovelSchema> = novel =>
  [
    `Title: ${withFallback(novel.attributes.title, 'Untitled Novel')}`,
    novel.attributes.author ? `Author: ${novel.attributes.author}` : undefined,
    novel.attributes.language ? `Language: ${novel.attributes.language}` : undefined,
    novel.attributes.tense ? `Tense: ${novel.attributes.tense}` : undefined,
    novel.attributes.pov
      ? `POV: ${novel.attributes.pov.type}${novel.attributes.pov.characterId ? ` (${novel.attributes.pov.characterId})` : ''
      }`
      : undefined,
    novel.attributes.labels?.length
      ? `Labels: ${novel.attributes.labels.map(label => label.label).join(', ')}`
      : undefined,
  ]
    .filter(Boolean)
    .join('\n');

export const renderSeriesPlain: PlainRenderer<NovelSeriesSchema> = series =>
  [
    `Series: ${withFallback(series.attributes.title, 'Untitled Series')}`,
    series.attributes.description ? `Description: ${series.attributes.description}` : undefined,
  ]
    .filter(Boolean)
    .join('\n');

export const renderActPlain: PlainRenderer<NovelActSchema> = (act, ctx, options) => {
  const tree = getNovelActTree(ctx.globalContext.generateStore.getState().novelActsTree, { actIds: [act.id] })
  if (tree.length) {
    return actTreeToPlain(tree, 'content', options?.listType);
  }
};

export const renderChapterPlain: PlainRenderer<NovelChapterSchema> = (chapter, ctx, options) => {
  const tree = getNovelActTree(ctx.globalContext.generateStore.getState().novelActsTree, { chapterIds: [chapter.id] })
  if (tree.length) {
    return actTreeToPlain(tree, 'content', options?.listType);
  }
};

export const renderScenePlain: PlainRenderer<NovelSceneSchema> = (scene, ctx, options) => {
  const tree = getNovelActTree(ctx.globalContext.generateStore.getState().novelActsTree, { sceneIds: [scene.id] })
  if (tree.length) {
    return actTreeToPlain(tree, 'content', options?.listType);
  }
};


export const renderCodexEntryPlain: PlainRenderer<CodexEntrySchema> = (entry, ctx, options) => {
  const listType = options?.listType;
  const aliases = entry.attributes.aliases ? ` (${entry.attributes.aliases})` : ''
  const title = `${entry.attributes.name}${aliases}`
  let textList = getCodexEntryFields(ctx, entry)
    .filter(d => d.type === CodexFieldType.Text)
    .map(d => d.value);

  if (entry.attributes.description) {
    textList.unshift(entry.attributes.description)
  }

  textList = textList.map(d => convertHtmlToFormattedText(d));

  // 如果没有列表类型，则直接返回标题
  if (!listType) {
    return `${title}` + (entry.attributes.description ? `: ${entry.attributes.description}` : '')
  }

  // 如果有列表类型，则返回列表
  let result = title
  if (textList.length === 1) {
    result += `: ${textList[0]}`
  } else {
    result += '\n'
    textList.forEach((d, i) => {
      const prefix = listType === 'ordered' ? `${i + 1}.` : '-';
      result += `\n  ${prefix} ${d}`
    })
  }
  return result
}

export const renderCodexCategoryPlain: PlainRenderer<CodexCategorySchema> = category =>
  `Codex Category: ${withFallback(category.attributes.name, 'Unnamed Category')}`;

export const renderCodexDetailPlain: PlainRenderer<CodexDetail> = detail =>
  [`Codex Detail: ${withFallback(detail.entry.attributes.name, detail.entry.id)}`, `Type: ${detail.entry.attributes.type}`].join('\n');

export const renderSnippetPlain: PlainRenderer<SnippetSchema> = snippet =>
  [
    `Snippet: ${withFallback(snippet.attributes.title, 'Untitled Snippet')}`,
    snippet.attributes.content ? convertJSONContentToFormattedText(snippet.attributes.content) : undefined,
  ]
    .filter(Boolean)
    .join('\n');

export const renderOutlineTreePlain: PlainRenderer<OutlineTree> = (outline, ctx, options) =>
  actTreeToPlain(outline.data, outline.mode, options?.listType);

export const renderLabelPlain: PlainRenderer<NovelLabel> = label =>
  `Label: ${label.data.label}${label.data.color ? ` (${label.data.color})` : ''}`;

export const renderPointOfViewPlain: PlainRenderer<PointOfView> = pov =>
  `POV: ${pov.value}${pov.characterId ? ` (${pov.characterId})` : ''}`;



export const plainRenderers = {
  renderNovelPlain,
  renderSeriesPlain,
  renderActPlain,
  renderChapterPlain,
  renderScenePlain,
  renderCodexEntryPlain,
  renderCodexCategoryPlain,
  renderSnippetPlain,
  renderOutlineTreePlain,
  renderLabelPlain,
  renderPointOfViewPlain,
  renderCodexDetailPlain,
}




